import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { useCurrentUserProfile } from '../../contexts/CurrentUserProfileContext';
import { useToast } from '../../contexts/ToastContext';
import { brightenScreen, restoreScreenBrightness } from '../../lib/native/priiPeople';
import { CodeView, useAddPeople } from './useAddPeople';
import './People.css';

/**
 * My code, a page of its own (Sam, 2026-10-30): the scanner is a full screen, and the code was a sheet over whatever
 * page you came from, so going from one to the other showed that page and then slid the sheet up. Now the two are
 * full screens that swap straight to each other: Scan here opens the camera over this page, closing the camera comes
 * back here, and the camera's My code button opens this page under it before it goes. Full brightness while it shows.
 */
export default function MyCodePage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { session, profile } = useCurrentUserProfile();
  const { startScan, sheets } = useAddPeople();

  useEffect(() => {
    brightenScreen();
    return () => {
      restoreScreenBrightness();
    };
  }, []);

  // It comes up from the bottom and goes back down, the way the Scan camera does (Sam, 2026-10-30)
  const [leaving, setLeaving] = useState(false);
  const close = () => {
    if (leaving) return;
    setLeaving(true);
    window.setTimeout(() => {
      if (window.history.length > 1) navigate(-1);
      else navigate('/profile', { replace: true });
    }, 260);
  };

  // On the screen itself, not in the page: the page wrapper animates (a transform), which would hold a fixed layer inside it
  return createPortal(
    <div className={`pp-codepage${leaving ? ' pp-codepage--out' : ''}`}>
      <div className="pp-codepage__top">
        <button
          type="button"
          className="pp-act__c btn-glass-icon btn-glass-icon-neutral"
          onClick={close}
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
        <b>My code</b>
        <span className="w-11" />
      </div>
      <div className="pp-codepage__body">
        {session?.user?.id ? (
          <CodeView
            me={session.user.id}
            name={profile?.display_name || ''}
            photo={profile?.profile_photo_url}
            onScan={startScan}
            showToast={showToast}
          />
        ) : (
          <p className="text-dark-muted">Log in to show your code.</p>
        )}
      </div>
      {sheets}
    </div>,
    document.body,
  );
}
